import { useRef } from 'react';
import { Box, IconButton } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useField } from 'react-final-form';

import { TextField } from '@occc-app/design-system';

const chatInputContainerSx = (variant: 'default' | 'large', theme: Theme): SxProps<Theme> => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  border: 1,
  borderColor: 'drive.border',
  boxShadow: 'none',
  backgroundColor: 'white',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  ...(variant === 'large' && {
    minHeight: 112,
  }),
  '&:focus-within': {
    borderColor: 'primary.main',
    boxShadow: `0px 4px 24px 0px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
});

const chatTextFieldSx = (variant: 'default' | 'large'): SxProps<Theme> => ({
  flex: 1,
  '& fieldset': { border: 'none' },
  '& .MuiOutlinedInput-input::placeholder': {
    fontWeight: 400,
  },
  ...(variant === 'default' && {
    '& .MuiOutlinedInput-root': { pr: 6 },
  }),
});

const sendButtonSx = (variant: 'default' | 'large'): SxProps<Theme> => ({
  borderRadius: '50%',
  p: 0.75,
  bgcolor: 'primary.main',
  color: 'white',
  '&:hover': {
    bgcolor: 'primary.dark',
  },
  ...(variant === 'default'
    ? {
        position: 'absolute',
        right: 8,
        bottom: 14,
      }
    : {
        alignSelf: 'flex-end',
        m: 1,
      }),
});

type ChatInputProps = {
  onSubmit: () => void;
  isStreaming?: boolean;
  variant?: 'default' | 'large';
};

export const ChatInput = ({
  onSubmit,
  isStreaming = false,
  variant = 'default',
}: ChatInputProps) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { input } = useField('question');
  const hasText = Boolean(input.value?.trim());
  const canSubmit = hasText && !isStreaming;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!isStreaming) {
        onSubmit();
      }
    }
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  return (
    <Box sx={chatInputContainerSx(variant, theme)} onClick={handleContainerClick}>
      <TextField
        name='question'
        inputRef={inputRef}
        multiline={true}
        minRows={1}
        maxRows={12}
        placeholder='Ask a question about your program...'
        inputProps={{ onKeyDown: handleKeyDown }}
        sx={chatTextFieldSx(variant)}
      />
      <IconButton
        onClick={onSubmit}
        color='primary'
        sx={sendButtonSx(variant)}
        disabled={!canSubmit}
      >
        <ArrowUpwardIcon sx={{ fontSize: 'body1.fontSize' }} />
      </IconButton>
    </Box>
  );
};
